import type {
  YardDashboardIndicatorRole,
  YardFieldType,
  YardFlow,
  YardStepCompletionTrigger,
  YardStepType,
} from 'contracts/VehicleJourney';

export const sanitizeFlowName = (value: string) =>
  value.replace(/[^a-zA-ZÀ-ÿ0-9_ ]/g, '');

export const normalizeCode = (value: string) =>
  sanitizeFlowName(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ /g, '_')
    .toUpperCase()
    .slice(0, 50);

export interface JourneyFieldOption {
  id: string;
  code: string;
  label: string;
  active: boolean;
}

export interface JourneyField {
  id: string;
  code: string;
  name: string;
  type: YardFieldType;
  required: boolean;
  active: boolean;
  placeholder: string;
  helpText: string;
  optionsText: string;
  options: JourneyFieldOption[];
}

export interface JourneyStep {
  id: string;
  yardStepId: number;
  yardRoleId: number | null;
  responsibleRole?: import('contracts/YardRoles').YardRole | null;
  code: string;
  name: string;
  type: YardStepType;
  interactionMode: 'START_FINISH' | 'CONFIRM';
  completionTrigger: YardStepCompletionTrigger;
  hasBip: boolean;
  hasCoordinates: boolean;
  hasReject: boolean;
  required: boolean;
  active: boolean;
  fields: JourneyField[];
}

export interface JourneyFlow {
  id: number | null;
  code: string;
  name: string;
  warehouseId: string;
  orderTypeId: string;
  carrierGroupId: string;
  active: boolean;
  steps: JourneyStep[];
}

export const STEP_TYPES: Array<{ value: YardStepType; label: string }> = [
  { value: 'COMUM', label: 'Comum' },
  { value: 'PORTARIA', label: 'Portaria' },
  { value: 'CHECKLIST', label: 'Checklist' },
  { value: 'ECLUSA', label: 'Eclusa' },
  { value: 'DOCA', label: 'Doca' },
  { value: 'PATIO_EXTERNO', label: 'Pátio externo' },
];

export const INDICATOR_ROLES: Array<{
  value: YardDashboardIndicatorRole;
  label: string;
}> = [
  { value: 'GATE_CHECK_IN', label: 'Check-in Portaria' },
  { value: 'CHECKLIST', label: 'Checklist' },
  { value: 'AIRLOCK_ENTRY', label: 'Eclusa - Entrada' },
  { value: 'AIRLOCK_EXIT', label: 'Eclusa - Saída' },
  { value: 'DOCK_PROCESS', label: 'Processo Doca' },
  { value: 'WAREHOUSE_INVOICE', label: 'Armazém - NF' },
  { value: 'WAREHOUSE_RECEIPT', label: 'Armazém - Canhoto' },
];

export const STEP_TYPE_LABELS: Record<YardStepType, string> = {
  COMUM: 'Comum',
  PORTARIA: 'Portaria',
  CHECKLIST: 'Checklist',
  ECLUSA: 'Eclusa',
  DOCA: 'Doca',
  PATIO_EXTERNO: 'Pátio externo',
};

export const FIELD_TYPES: Array<{ value: YardFieldType; label: string }> = [
  { value: 'TEXT', label: 'Texto' },
  { value: 'TEXTAREA', label: 'Texto longo' },
  { value: 'NUMBER', label: 'Número' },
  { value: 'SELECT', label: 'Seleção' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'CHECKBOX_GROUP', label: 'Grupo de checkboxes' },
  { value: 'DATE', label: 'Data' },
  { value: 'TIME', label: 'Hora' },
  { value: 'DATETIME', label: 'Data e hora' },
];

export const emptyJourneyFlow = (): JourneyFlow => ({
  id: null,
  code: '',
  name: '',
  warehouseId: '',
  orderTypeId: '',
  carrierGroupId: '',
  active: true,
  steps: [],
});

export const toJourneyFlow = (flow: YardFlow): JourneyFlow => ({
  id: flow.id,
  code: flow.code,
  name: flow.name,
  warehouseId: flow.warehouseId === null ? '' : String(flow.warehouseId),
  orderTypeId: String(flow.orderTypeId),
  carrierGroupId: String(flow.carrierGroupId),
  active: flow.active,
  steps: (flow.flowSteps || []).map((flowStep) => ({
    id: String(flowStep.id),
    yardStepId: flowStep.yardStepId,
    yardRoleId: flowStep.yardRoleId ?? null,
    responsibleRole: flowStep.responsibleRole ?? null,
    code: flowStep.step.code,
    name: flowStep.step.name,
    type: flowStep.step.type,
    interactionMode: flowStep.interactionMode,
    completionTrigger: flowStep.completionTrigger ?? null,
    hasBip: flowStep.hasBip,
    hasCoordinates:
      flowStep.completionTrigger === 'ENTRY_AUTHORIZED'
        ? false
        : flowStep.hasCoordinates,
    hasReject: flowStep.hasReject,
    required: flowStep.required,
    active:
      flowStep.completionTrigger === 'ENTRY_AUTHORIZED'
        ? true
        : flowStep.active,
    fields: (flowStep.fields || []).map((field) => ({
      id: String(field.id),
      code: field.code,
      name: field.label,
      type: field.fieldType,
      required:
        flowStep.completionTrigger === 'ENTRY_AUTHORIZED'
          ? false
          : field.required,
      active: field.active,
      placeholder: field.placeholder || '',
      helpText: field.helpText || '',
      optionsText: (field.options || [])
        .map((option) => option.label)
        .join(', '),
      options: (field.options || []).map((option) => ({
        id: String(option.id),
        code: option.code,
        label: option.label,
        active: option.active,
      })),
    })),
  })),
});
